// 品牌统一入口：语言字段在同目录维护，逻辑与显示映射在此集中管理。
import * as simplified from "./zh-CN.js";
import * as traditional from "./zh-HK.js";
import * as english from "./en-US.js";
export const zhCN = simplified.messages;
export const zhHK = traditional.messages;
export const enUS = english.messages;

// 分区导出保留原有接口，词条只在语言文件中保存一份。
export const pcBrandZhCN = simplified.ui;
export const pcBrandZhHK = traditional.ui;
export const pcBrandEnUS = english.ui;

const brands = Object.freeze(
  Object.fromEntries(
    Object.keys(simplified.brandNames).map((key) => [
      key,
      Object.freeze([traditional.brandNames[key], english.brandNames[key]]),
    ])
  )
);

const categories = Object.freeze(
  Object.fromEntries(
    Object.keys(simplified.categoryNames).map((key) => [
      key,
      Object.freeze([
        traditional.categoryNames[key],
        english.categoryNames[key],
      ]),
    ])
  )
);

const regions = Object.freeze(
  Object.fromEntries(
    Object.keys(simplified.regionNames).map((key) => [
      key,
      Object.freeze([traditional.regionNames[key], english.regionNames[key]]),
    ])
  )
);

function display(table, value, locale) {
  const raw = String(value ?? "");
  if (!Object.hasOwn(table, raw)) return raw;
  return locale === "zh-HK"
    ? table[raw][0]
    : locale === "en-US"
    ? table[raw][1]
    : raw;
}

export function brandDisplayLabel(brand, locale) {
  if (
    locale === "en-US" &&
    typeof brand?.nameEn === "string" &&
    brand.nameEn.trim()
  )
    return brand.nameEn;
  return display(
    brands,
    typeof brand === "string" ? brand : brand?.name,
    locale
  );
}

export function brandCategoryLabel(value, locale) {
  const extra = {
    压敏电阻与气体放电管: "pcBrand.categoryVaristorGdt",
    二极管: "pcBrand.categoryDiodes", 三极管: "pcBrand.categoryTransistors",
    电阻: "pcBrand.categoryResistors", 电容: "pcBrand.categoryCapacitors", 电感: "pcBrand.categoryInductors",
  };
  if (Object.hasOwn(extra, value)) return metadataLabel(extra[value], locale);
  return display(categories, value, locale);
}

export function brandRegionLabel(value, locale) {
  const extra = {
    英国: "pcBrand.regionUnitedKingdom", 德国: "pcBrand.regionGermany", 法国: "pcBrand.regionFrance",
    新加坡: "pcBrand.regionSingapore", 中国香港: "pcBrand.regionHongKong",
  };
  if (Object.hasOwn(extra, value)) return metadataLabel(extra[value], locale);
  return display(regions, value, locale);
}

function metadataLabel(key, locale) {
  return (locale === "en-US" ? enUS : locale === "zh-HK" ? zhHK : zhCN)[key];
}

// 仅匹配已登记的系统固定文案，未知业务原文不转换。
const brandUiKeyIndex = Object.freeze({
  返回: "pcBrand.back",
  未知: "pcBrand.unknown",
  未分类: "pcBrand.uncategorized",
  认领品牌: "pcBrand.claimBrand",
  品牌管理: "pcBrand.brandManagement",
  品牌简介: "pcBrand.brandIntroduction",
  暂无品牌简介: "pcBrand.noBrandIntroduction",
  企业介绍: "pcBrand.companyIntroduction",
  暂无企业介绍: "pcBrand.noCompanyIntroduction",
  进入产品数据: "pcBrand.openProductData",
  产品图片: "pcBrand.productImage",
  "已售:": "pcBrand.sold",
  暂无产品数据: "pcBrand.noProductData",
  关联企业: "pcBrand.relatedCompanies",
  自有品牌: "pcBrand.brandOwner",
  代理商: "pcBrand.distributor",
  贸易商: "pcBrand.trader",
  有效: "pcBrand.active",
  禁用: "pcBrand.disabled",
  未知类型: "pcBrand.unknownType",
  进入公司主页: "pcBrand.visitCompany",
  暂无关联企业: "pcBrand.noRelatedCompanies",
  暂无该类型关联企业: "pcBrand.noRelatedCompaniesOfThisType",
  关联商品系列: "pcBrand.relatedProductFamilies",
  "1 个型号": "pcBrand.value1Model",
  关联订单: "pcBrand.relatedOrders",
  订单数: "pcBrand.orders",
  成交数量: "pcBrand.tradedQuantity",
  成交金额: "pcBrand.tradeValue",
  成交地区: "pcBrand.tradingRegions",
  时间: "pcBrand.time",
  成交型号: "pcBrand.tradedModel",
  产品: "pcBrand.product",
  地区: "pcBrand.region",
  数量: "pcBrand.quantity",
  交易额: "pcBrand.transactionValue",
  创建时间: "pcBrand.createdAt",
  品牌优势: "pcBrand.brandStrengths",
  技术领先: "pcBrand.leadingTechnology",
  拥有行业领先的研发技术和专利: "pcBrand.industryLeadingRDCapabilitiesAndPatents",
  品质保证: "pcBrand.qualityAssurance",
  "严格的质量控制体系，确保产品品质":
    "pcBrand.rigorousQualityControlsToEnsureProductQuality",
  市场领先: "pcBrand.marketLeadership",
  在行业内拥有较高的市场份额: "pcBrand.aStrongMarketShareInTheIndustry",
  完善服务: "pcBrand.comprehensiveService",
  提供全方位的技术支持和售后服务: "pcBrand.comprehensiveTechnicalSupportAndAfterSalesService",
  公司动态: "pcBrand.companyNews",
  合作伙伴: "pcBrand.partners",
  相关品牌: "pcBrand.relatedBrands",
  "更多 ▼": "pcBrand.more",
  "收起 ▲": "pcBrand.collapse",
  "北京0家 | 上海0家 | 广州0家 | 深圳0家":
    "pcBrand.beijing0Shanghai0Guangzhou0Shenzhen0",
  应用领域: "pcBrand.applications",
  商标注册证书: "pcBrand.trademarkRegistrationCertificates",
  企业名称: "pcBrand.companyName",
  请输入企业名称: "pcBrand.enterCompanyName",
  企业类型: "pcBrand.companyType",
  商标证书: "pcBrand.trademarkCertificate",
  代理证书: "pcBrand.distributorAuthorizationCertificate",
  上传商标证书: "pcBrand.uploadTrademarkCertificate",
  上传代理证书: "pcBrand.uploadAuthorizationCertificate",
  取消: "pcBrand.cancel",
  确认: "pcBrand.confirm",
  企业名称不能为空: "pcBrand.companyNameIsRequired",
  请选择企业类型: "pcBrand.selectCompanyType",
  商标编号: "pcBrand.trademarkNumber",
  总部: "pcBrand.headquarters",
  产品线: "pcBrand.productLines",
  授权区域: "pcBrand.authorizedRegions",
  授权期限: "pcBrand.authorizationPeriod",
  主营型号: "pcBrand.mainModels",
  月供能力: "pcBrand.monthlySupplyCapacity",
  中国: "pcBrand.china",
  深圳: "pcBrand.shenzhen",
  华东区: "pcBrand.eastChina",
  华南区: "pcBrand.southChina",
  "上海、江苏、浙江、安徽": "pcBrand.shanghaiJiangsuZhejiangAnhui",
  "微控制器、电源管理、传感器、连接芯片":
    "pcBrand.microcontrollersPowerManagementSensorsConnectivityChips",
  "2026-01-01 至 2026-12-31": "pcBrand.value20260101To20261231",
  认领成功: "pcBrand.brandClaimedSuccessfully",
  品牌ID不能为空: "pcBrand.brandIdIsRequired",
  获取品牌详情失败: "pcBrand.couldNotLoadBrandDetails",
  品牌信息: "pcBrand.brandInformation",
  品牌名称: "pcBrand.brandName",
  请输入品牌中文名称: "pcBrand.enterTheChineseBrandName",
  选择品牌: "pcBrand.selectBrand",
  英文名称: "pcBrand.englishName",
  请输入品牌英文名称: "pcBrand.enterTheEnglishBrandName",
  品牌地区: "pcBrand.brandRegion",
  请选择品牌地区: "pcBrand.selectBrandRegion",
  品牌分类: "pcBrand.brandCategory",
  请选择品牌分类: "pcBrand.selectBrandCategory",
  成立年份: "pcBrand.yearFounded",
  请输入品牌简介: "pcBrand.enterBrandIntroduction",
  请输入企业介绍: "pcBrand.enterCompanyIntroduction",
  智能安防: "pcBrand.smartSecurity",
  家用电器: "pcBrand.homeAppliances",
  智能物联: "pcBrand.smartIot",
  医疗电子: "pcBrand.medicalElectronics",
  通讯网络: "pcBrand.communicationsNetworks",
  能源控制: "pcBrand.energyControl",
  消费电子: "pcBrand.consumerElectronics",
  工业控制: "pcBrand.industrialControl",
  汽车电子: "pcBrand.automotiveElectronics",
  品牌Logo: "pcBrand.brandLogo",
  上传Logo: "pcBrand.uploadLogo",
  联系信息: "pcBrand.contactInformation",
  隐私保护: "pcBrand.privacyProtection",
  联系人: "pcBrand.contact",
  请输入联系人姓名: "pcBrand.enterContactName",
  联系电话: "pcBrand.contactPhone",
  请输入联系电话: "pcBrand.enterContactPhone",
  电子邮箱: "pcBrand.email",
  请输入电子邮箱: "pcBrand.enterEmailAddress",
  重置: "pcBrand.reset",
  提交申请: "pcBrand.submitApplication",
  品牌名称不能为空: "pcBrand.brandNameIsRequired",
  品牌地区不能为空: "pcBrand.brandRegionIsRequired",
  品牌分类不能为空: "pcBrand.brandCategoryIsRequired",
  创建年份不能为空: "pcBrand.yearFoundedIsRequired",
  提交成功: "pcBrand.submittedSuccessfully",
  "成立于{year}年": "pcBrand.foundedInYear",
  "产品列表（{count}）": "pcBrand.productsCount",
  "展开（{count}条）▼": "pcBrand.expandCountMore",
  "更多（{count}个）▼": "pcBrand.moreCount",
  "{count} 个系列": "pcBrand.countFamilies",
  "{count} 笔成交": "pcBrand.countTrades",
  "{shown} / {total} 家企业": "pcBrand.shownTotalCompanies",
  "请上传{type}": "pcBrand.uploadType",
  "{brand}发布最新产品": "pcBrand.brandLaunchesNewProducts",
  "{brand}今日发布最新产品，为市场带来创新解决方案。":
    "pcBrand.brandLaunchedNewProductsTodayBringingInnovativeSolutionsToTheMarket",
  "{brand}宣布战略合作伙伴关系": "pcBrand.brandAnnouncesStrategicPartnerships",
  "{brand}与多家企业建立战略合作伙伴关系，共同推动行业发展。":
    "pcBrand.brandHasFormedStrategicPartnershipsWithCompaniesToAdvanceTheIndustry",
  "{brand}2025年业绩增长": "pcBrand.brandReportsGrowthIn2025",
  "{brand}2025年业绩持续增长，市场份额持续扩大。":
    "pcBrand.brandGrewIn2025AndExpandedItsMarketShare",
  中国区总代理: "pcBrand.chinaMasterDistributor",
  华东区代理: "pcBrand.eastChinaDistributor",
  华南区代理: "pcBrand.southChinaDistributor",
  全国贸易商: "pcBrand.nationwideTrader",
  区域贸易商: "pcBrand.regionalTrader",
  授权贸易商: "pcBrand.authorizedTrader",
});
export const brandUiKey = (value) =>
  typeof value === "string" && Object.hasOwn(brandUiKeyIndex, value)
    ? brandUiKeyIndex[value]
    : null;
